<template>
  <el-config-provider :locale="elementPlusLocale">
    <div id="app">
      <router-view />
    </div>
  </el-config-provider>
</template>

<script>
import { computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import zhCn from 'element-plus/es/locale/lang/zh-cn'
import zhTw from 'element-plus/es/locale/lang/zh-tw'
import en from 'element-plus/es/locale/lang/en'
import { auth } from '@/utils/auth'
import { updateDocumentTitle } from '@/utils/documentTitle'

export default {
  name: 'App',
  setup() {
    const { locale } = useI18n()
    
    // 根据 i18n 语言动态获取 Element Plus 语言包
    const elementPlusLocale = computed(() => {
      if (locale.value === 'zh-tw') {
        return zhTw
      } else if (locale.value === 'zh-cn') {
        return zhCn
      } else {
        return en
      }
    })

    // 语言切换后同步浏览器标题（固定为深圳市版权公共服务平台 / 多语言对应）
    watch(locale, () => {
      updateDocumentTitle()
    })

    // 无感刷新：页面加载时若已登录则拉取一次当前主体信息，保持缓存与后端一致
    onMounted(async () => {
      updateDocumentTitle()
      if (auth.isLoggedIn()) {
        await auth.refreshMainBody()
      }
    })
    
    return {
      elementPlusLocale
    }
  }
}
</script>

<style lang="scss">
html, body, #app {
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* 全局统一 Element Plus 主题色（primary） */
:root {
  --el-color-primary: #0D62F3 !important;
  /* 常用派生色（Element Plus 会用到 light / dark 变体） */
  --el-color-primary-dark-2: #0B58DB !important;
  --el-color-primary-light-3: #3D82F6 !important;
  --el-color-primary-light-5: #6AA1F8 !important;
  --el-color-primary-light-7: #9AC0FB !important;
  --el-color-primary-light-8: #B2D0FC !important;
  --el-color-primary-light-9: #D9E7FE !important;
}

/* 全局统一 Element Plus 下拉选中项高亮色 */
.el-select-dropdown__item.is-selected {
  color: #0D62F3 !important;
}

/* 全局输入框默认样式（参考 ResetPassword.vue .form-input，去除宽度设置） */
.el-input {
  height: 40px;
}

.el-input .el-input__wrapper {
  width: 100%;
  height: 40px;
  border-radius: 6px;
  border: 1px solid #E8E8E8 !important;
  box-shadow: none !important;
  box-sizing: border-box;
  background-color: #fff;
  padding: 0 15px;
}

.el-input .el-input__wrapper:hover {
  border-color: #E8E8E8 !important;
}

.el-input .el-input__wrapper.is-focus {
  border-color: #0D62F3 !important;
}

.el-input .el-input__inner {
  color: #333333;
  font-size: 14px;
}

/* 全局统一 el-select / el-cascader 触发区（与用户中心表单 .form-input 一致：40px、#E8E8E8、圆角 6px） */
.el-cascader {
  height: 40px;
}

.el-select:not(.el-select--multiple) {
  height: 40px;
}

.el-select .el-select__wrapper,
.el-cascader .el-cascader__wrapper {
  width: 100%;
  min-height: 40px;
  border-radius: 6px;
  border: 1px solid #e8e8e8 !important;
  box-shadow: none !important;
  box-sizing: border-box;
  background-color: #fff;
  padding: 0 15px;
}

.el-select:not(.el-select--multiple) .el-select__wrapper {
  height: 40px;
}

.el-select.el-select--multiple .el-select__wrapper {
  height: auto;
  min-height: 40px;
}

.el-select .el-select__wrapper:hover,
.el-cascader .el-cascader__wrapper:hover {
  border-color: #e8e8e8 !important;
}

.el-select .el-select__wrapper.is-focused,
.el-select .el-select__wrapper.is-focus,
.el-cascader .el-cascader__wrapper.is-focused {
  border-color: #0D62F3 !important;
}

.el-select .el-select__selected-item {
  color: #333333;
  font-size: 14px;
}

.el-select .el-select__placeholder {
  font-size: 14px;
}

/* 下拉面板（挂载到 body，与选中项主色一致） */
.el-select-dropdown {
  border-radius: 6px;
}

.el-select-dropdown__item {
  font-size: 14px;
}

.el-select-dropdown__item.is-hovering {
  background-color: #f5f7fa !important;
}

/* 日期/时间选择器（el-date-picker / el-time-picker）：与输入框、下拉同一套 40px、#E8E8E8、圆角 6px */
/* 单选：外层为 .el-input.el-date-editor，已由上方 .el-input .el-input__wrapper 覆盖 */
.el-input.el-date-editor {
  height: 40px;
}

/* 范围选择：根节点即为 .el-date-editor.el-input__wrapper（无外层 .el-input），需单独写 */
.el-date-editor.el-input__wrapper {
  min-height: 40px;
  height: 40px;
  border-radius: 6px;
  border: 1px solid #e8e8e8 !important;
  box-shadow: none !important;
  box-sizing: border-box;
  background-color: #fff;
  padding: 0 15px;
}

.el-date-editor.el-input__wrapper:hover {
  border-color: #e8e8e8 !important;
}

.el-date-editor.el-input__wrapper.is-active,
.el-date-editor.el-input__wrapper:focus-within {
  border-color: #0D62F3 !important;
}

.el-date-editor .el-range-input {
  color: #333333;
  font-size: 14px;
}

/* 弹出面板（挂载到 body） */
.el-picker__popper.el-popper {
  border-radius: 6px;
}

/* 全局统一 Element Plus 分页当前页数字高亮色 */
.el-pagination .el-pager li.is-active {
  color: #0D62F3 !important;
  border-color: #0D62F3 !important;
}

/* 全局默认表格（参照 UserManagement.vue） */
.el-table {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #ebeef5;
  box-sizing: border-box;
}

.el-table .el-table__inner-wrapper {
  border-radius: inherit;
}

.el-table th.el-table__cell:first-child {
  border-top-left-radius: 8px;
}

.el-table th.el-table__cell:last-child {
  border-top-right-radius: 8px;
}

.el-table .el-table__body-wrapper tbody tr:last-child td.el-table__cell:first-child {
  border-bottom-left-radius: 8px;
}

.el-table .el-table__body-wrapper tbody tr:last-child td.el-table__cell:last-child {
  border-bottom-right-radius: 8px;
}

.el-table th.el-table__cell {
  background-color: #f5f7fa !important;
  color: #333;
  font-weight: 600;
  padding: 12px 0;
  text-align: center;
}

.el-table td.el-table__cell {
  font-weight: 400;
  color: #191919;
  text-align: center;
  padding: 12px 0;
}

.el-table .cell {
  text-align: center;
}

.el-table .el-button {
  border-radius: 4px;
}

/* 列表页公共布局与表单样式（参照 UserManagement.vue，页面根节点按需使用对应 class） */
.page-card {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  padding: 0 0 40px;
}

.filter-segment-wrap,
.user-type-segment-wrap {
  padding: 4px 0 23px;
}

.filter-segment,
.user-type-segment {
  display: inline-flex;
  flex-wrap: wrap;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
}

.filter-segment .segment-item,
.user-type-segment .segment-item {
  min-width: 104px;
  height: 36px;
  padding: 0 20px;
  border: none;
  background: #fff;
  color: #0d62f3;
  font-size: 14px;
  font-weight: 500;
  line-height: 36px;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
}

.filter-segment .segment-item:not(:last-child),
.user-type-segment .segment-item:not(:last-child) {
  border-right: 1px solid #dcdfe6;
}

.filter-segment .segment-item.active,
.user-type-segment .segment-item.active {
  background: #0d62f3;
  color: #fff;
}

.filter-segment .segment-item:hover:not(.active),
.user-type-segment .segment-item:hover:not(.active) {
  background: #f0f6ff;
}

.search-section {
  margin-bottom: 16px;
  padding: 0;
  box-sizing: border-box;
}

/* 多行搜索布局（含 .search-row 的列表页，如 UserManagement） */
.search-form:has(.search-row) {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.search-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px 68px;
}

.search-row--second {
  align-items: center;
}

.search-field.el-form-item {
  margin-bottom: 0;
  margin-right: 0;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.search-field .el-form-item__label {
  width: 84px;
  padding-right: 12px;
  box-sizing: border-box;
  font-size: 14px;
  font-weight: 500;
  color: #191919;
  text-align: left;
  justify-content: flex-start;
  flex-shrink: 0;
}

.search-field .el-form-item__content {
  margin-left: 0 !important;
  flex-shrink: 0;
}

.search-field .search-input {
  width: 280px;
  min-width: 280px;
}

.search-field .search-select {
  width: 280px;
  min-width: 280px;
}

.search-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  margin-left: auto;
}

.btn-query.el-button {
  padding: 0 12px;
  border-radius: 4px;
  font-size: 13px;
  background: #0d62f3 !important;
  border-color: #0d62f3 !important;
}

.btn-query.el-button:hover {
  background: #0b56e0 !important;
  border-color: #0b56e0 !important;
}

.btn-query .el-icon {
  margin-right: 4px;
  font-size: 14px;
}

.btn-reset.el-button {
  padding: 0 12px;
  border-radius: 4px;
  font-size: 13px;
  color: #191919;
  background: #ffffff !important;
  border: 1px solid #e8e8e8 !important;
}

.btn-reset.el-button:hover {
  border-color: #c9cdd4 !important;
  background: #f7f8fa !important;
  color: #191919 !important;
}

.btn-reset .el-icon {
  margin-right: 4px;
  font-size: 14px;
  color: #0d62f3;
}

.action-bar {
  padding: 0 0 20px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
}

.pagination-section {
  padding-top: 26px;
  padding-bottom: 4px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

@media (max-width: 992px) {
  .search-actions {
    width: 100%;
    justify-content: flex-start;
    margin-left: 0;
  }
}

/* 全局统一 Radio（el-radio-group / el-radio）样式 */
// .el-radio-group {
//   display: inline-flex;
//   align-items: center;
//   gap: 32px;
//   flex-wrap: wrap;
// }

.el-radio {
  margin-right: 0 !important;
  display: inline-flex;
  align-items: center;
  height: auto;
}

.el-radio .el-radio__label {
  padding-left: 8px;
  color: #909399;
  font-size: 14px;
}

.el-radio .el-radio__input .el-radio__inner {
  width: 16px;
  height: 16px;
  border-width: 2px;
  border-color: #c0c4cc;
  background-color: #fff;
}

.el-radio .el-radio__input .el-radio__inner::after {
  width: 6px;
  height: 6px;
  background-color: #fff;
}

.el-radio.is-checked .el-radio__input .el-radio__inner {
  border-width: 2px;
  border-color: #0D62F3;
  background-color: #fff;
}

.el-radio.is-checked .el-radio__input .el-radio__inner::after {
  width: 8px;
  height: 8px;
  background-color: #0D62F3;
}

.el-radio.is-checked .el-radio__label {
  color: #0D62F3;
}

/* 全局统一 Checkbox（el-checkbox-group / el-checkbox）样式 */
:root {
  --el-checkbox-checked-bg-color: #0D62F3 !important;
  --el-checkbox-checked-input-border-color: #0D62F3 !important;
  --el-checkbox-checked-text-color: #0D62F3 !important;
}

.el-checkbox .el-checkbox__inner {
  border-width: 2px !important;
}

.el-checkbox .el-checkbox__input.is-checked .el-checkbox__inner {
  background-color: #0D62F3 !important;
  border-color: #0D62F3 !important;
  border-width: 2px !important;
}

.el-checkbox .el-checkbox__input.is-indeterminate .el-checkbox__inner {
  background-color: #0D62F3 !important;
  border-color: #0D62F3 !important;
  border-width: 2px !important;
}

/* 全局统一 Element Plus 红色按钮（danger）：品牌色 #F3300D + 白字保证对比（含 is-plain 被覆盖为实心底的情况） */
.el-button--danger {
  --el-button-hover-bg-color: #d92b0c;
  --el-button-hover-border-color: #d92b0c;
  background-color: #F3300D !important;
  border-color: #F3300D !important;
  color: #ffffff !important;
}
.el-button--danger:hover,
.el-button--danger:focus-visible {
  background-color: var(--el-button-hover-bg-color) !important;
  border-color: var(--el-button-hover-border-color) !important;
  color: #ffffff !important;
}
.el-button--danger.is-disabled,
.el-button--danger.is-disabled:hover {
  cursor: not-allowed;
  opacity: 0.5 !important;
  background-color: #F3300D !important;
  border-color: #F3300D !important;
  color: #ffffff !important;
}

/* 全局 el-dialog 样式优化（与增强确认弹窗头部样式保持一致） */
.el-dialog {
  border-radius: 7px !important;
  overflow: hidden !important;
  padding: 0 !important; /* 移除外层默认的 padding */
}

.el-dialog__header {
  padding: 14px 22px !important;
  background-color: #F0F3F7 !important;
  margin: 0 !important;
  border-bottom: none !important;
  display: flex !important;
  align-items: center !important;
}

/* 未设置标题时不应用头部背景与留白，避免出现空头部条 */
.el-dialog__header:has(.el-dialog__title:empty) {
  padding: 0 !important;
  background-color: transparent !important;
}

.el-dialog__title {
  font-size: 15px !important;
  font-weight: 500 !important;
  color: #191919 !important;
  line-height: 22px !important;
  margin: 0 !important;
}

.el-dialog__headerbtn {
  top: 14px !important;
  right: 14px !important;
  height: 22px !important;
  width: 22px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.el-dialog__headerbtn .el-dialog__close {
  font-size: 16px !important;
  color: #191919 !important;
  transition: color 0.2s;
}

.el-dialog__headerbtn:hover .el-dialog__close,
.el-dialog__headerbtn:focus .el-dialog__close {
  color: #0D62F3 !important;
}

.el-dialog__headerbtn .el-dialog__close svg {
  stroke: currentColor;
  stroke-width: 50;
}

.el-dialog__body {
  padding: 24px 22px !important;
}

.el-dialog__footer {
  padding: 0 22px 22px !important;
  border-top: none !important;
}

/* 自定义增强确认弹窗样式 */
.custom-enhanced-confirm-dialog {
  width: var(--enhanced-confirm-dialog-width, 380px) !important;
  max-width: 90vw !important;
  border-radius: 7px !important;
  padding: 0 0 22px 0 !important;
  border: none !important;
  overflow: hidden !important;
}

.custom-enhanced-confirm-dialog .el-message-box__header {
  padding: 14px 22px !important;
  background-color: #F0F3F7 !important;
  margin: 0 !important;
}

.custom-enhanced-confirm-dialog .el-message-box__title {
  font-size: 15px !important;
  font-weight: 500 !important;
  color: #191919 !important;
  justify-content: flex-start !important;
  line-height: 22px !important;
}

.custom-enhanced-confirm-dialog .el-message-box__headerbtn {
  top: 14px !important;
  right: 14px !important;
  height: 22px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.custom-enhanced-confirm-dialog .el-message-box__headerbtn .el-message-box__close {
  font-size: 16px !important;
  color: #191919 !important;
  transition: color 0.2s;
}

.custom-enhanced-confirm-dialog .el-message-box__headerbtn:hover .el-message-box__close,
.custom-enhanced-confirm-dialog .el-message-box__headerbtn:focus .el-message-box__close {
  color: #0D62F3 !important;
}

.custom-enhanced-confirm-dialog .el-message-box__headerbtn .el-message-box__close svg {
  stroke: currentColor;
  stroke-width: 50;
}

.custom-enhanced-confirm-dialog .el-message-box__content {
  padding: 36px 22px !important;
  text-align: center !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  color: #191919 !important;
}

.custom-enhanced-confirm-dialog .el-message-box__container {
  display: block !important;
}

.custom-enhanced-confirm-dialog .el-message-box__status {
  display: none !important;
}

.custom-enhanced-confirm-dialog .el-message-box__message {
  padding: 0 !important;
}

.custom-enhanced-confirm-dialog .el-message-box__btns {
  padding: 0 22px !important;
  justify-content: center !important;
  gap: 14px !important;
}

.custom-enhanced-confirm-dialog .el-message-box__btns .el-button {
  width: 116px !important;
  height: 34px !important;
  border-radius: 3px !important;
  font-size: 13px !important;
  margin: 0 !important;
}

.custom-enhanced-confirm-dialog .el-message-box__btns .el-button:first-child {
  background: #FFFFFF !important;
  border: 1px solid #0D62F3 !important;
  color: #0D62F3 !important;
}

.custom-enhanced-confirm-dialog .el-message-box__btns .el-button:first-child:hover {
  background: rgba(13, 98, 243, 0.05) !important;
}

.custom-enhanced-confirm-dialog .el-message-box__btns .el-button:last-child {
  background: #0D62F3 !important;
  border: 1px solid #0D62F3 !important;
  color: #FFFFFF !important;
}

.custom-enhanced-confirm-dialog .el-message-box__btns .el-button:last-child:hover {
  background: #3D82F6 !important;
  border-color: #3D82F6 !important;
}

/* 全局 ElMessage 样式优化 - 契合系统极简卡片化风格 */
.el-message {
  min-width: unset !important;
  padding: 12px 24px !important;
  border-radius: 8px !important;
  border: 1px solid #e8e8e8 !important;
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.05) !important;
  background-color: #ffffff !important;
}

.el-message .el-message__content {
  font-size: 14px !important;
  font-weight: 500 !important;
  color: #191919 !important; /* 采用系统的极简深色字体 */
}

/* 统一各类提示图标尺寸与系统色值 */
.el-message .el-message__icon {
  font-size: 18px !important;
}

/* 成功：系统内代表著作权的鲜亮绿 */
.el-message--success .el-message__icon {
  color: #1FD430 !important; 
}

/* 错误：系统内用的危险红 */
.el-message--error .el-message__icon {
  color: #F3300D !important;
}

/* 警告：系统内用的橙黄 */
.el-message--warning .el-message__icon {
  color: #FFA940 !important;
}

/* 提示：系统主色调（主题蓝） */
.el-message--info .el-message__icon {
  color: #0D62F3 !important;
}

/* 全局 ECharts 提示框（Tooltip）透明与毛玻璃效果 */
/* ECharts 默认未提供特定 class，通过其固有的内联样式特征（z-index: 9999999）进行精准覆盖 */
div[style*="z-index: 9999999"][style*="position: absolute"][style*="pointer-events: none"] {
  background: rgba(255, 255, 255, 0.65) !important;
  backdrop-filter: blur(10px) !important;
  -webkit-backdrop-filter: blur(10px) !important;
  border: 1px solid rgba(255, 255, 255, 0.5) !important;
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.08) !important;
  border-radius: 8px !important;
  color: #333 !important;
}
</style>
